import { RowData } from "@tanstack/react-table";
import { FC, ReactElement, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  TableWindowWrapperContext,
  usePageContext,
  usePageScrollPositionMemory,
  useWatchingSelectedIds,
} from "@ryde/redux";
import {
  appLanguageSelector,
  buildColumnVisibilityLayoutSignature,
  buildTableColumnDefsLayoutSignature,
  getEntityEditHrefBase,
  groupWithFilteredRowsSF,
  IColumnDef,
  IExtendableDto,
  isGridSingleSelectViewPathname,
  isRidesEditOrAddRoutePathname,
  NO_GROUPING_QS_VALUE,
  RenderSubComponentModel,
  ScrollAreaEnum,
  serializeSortingStateQs,
  tableColumnsOrderStateAF,
  tableColumnsSortingAF,
  tableColumnsVisibilityAF,
  tableGridFetchingAtomFamily,
  tableGroupingAF,
  usePrefixedRouting,
} from "@ryde/shared";

import { IGridRowProps } from "../../core";
import { GridNoData } from "../../core/grid.no-data";
import { SearchHighlightProvider } from "./search-highlight-context";
import { TableItemWrapper } from "./table-item-wrapper";
import { TableWrapperVariableSizeList } from "./table-wrapper-variable-size-list";
import { TableWrapperContext } from "./table-wrapper.context";
import { TableItemNoGroup } from "./table.item-no-group";

interface ITableContextWrapper<T extends IExtendableDto & RowData> {
  tableDefs: IColumnDef<T, unknown>[];
  GridRowRenderer?: FC<IGridRowProps<T>>;
  RenderSubComponent?: RenderSubComponentModel<T>;
  onScrollEnd?: () => void;
}

export const TableContextWrapper = <T extends IExtendableDto & RowData>({
  tableDefs,
  GridRowRenderer,
  RenderSubComponent,
  onScrollEnd,
}: ITableContextWrapper<T>): ReactElement => {
  const { entity } = usePageContext();
  const [columnSorting, setColumnSorting] = useRecoilState(
    tableColumnsSortingAF(entity),
  );
  const [columnGrouping, setColumnGrouping] = useRecoilState(
    tableGroupingAF(entity),
  );

  const [columnOrder, setColumnOrder] = useRecoilState(
    tableColumnsOrderStateAF(entity),
  );

  const [columnVisibility, setColumnVisibility] = useRecoilState(
    tableColumnsVisibilityAF(entity),
  );
  const groupWithFilteredRows = useRecoilValue(groupWithFilteredRowsSF(entity));
  const isTableGridFetching = useRecoilValue(
    tableGridFetchingAtomFamily(entity),
  );
  const language = useRecoilValue(appLanguageSelector);

  const columnGroupingSignature = columnGrouping?.join(".") ?? "";

  const groupingField = useMemo(() => {
    if (
      !columnGrouping?.length ||
      columnGrouping?.[0] === NO_GROUPING_QS_VALUE ||
      columnGrouping?.[0] === undefined
    ) {
      return undefined;
    }

    return columnGrouping?.[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnGroupingSignature is a stable proxy for grouping column changes
  }, [columnGroupingSignature]);
  const columnOrderSignature = columnOrder.join(".");
  const columnSortingSignature = (
    serializeSortingStateQs(columnSorting) ?? []
  ).join(".");
  const columnVisibilitySignature =
    buildColumnVisibilityLayoutSignature(columnVisibility);
  const tableDefsSignature = `${language}:${buildTableColumnDefsLayoutSignature(tableDefs)}`;

  // Route facts computed once here so rows never instantiate useRidesNavigation()
  // (~20 Recoil subscriptions plus a router subscription, per row, before this).
  const location = useLocation();
  const { addPrefix } = usePrefixedRouting();
  const isGridSingleSelectView = isGridSingleSelectViewPathname(
    location.pathname,
  );
  const isRidesEditOrAddRoute = isRidesEditOrAddRoutePathname(
    location.pathname,
  );
  const editHrefBase = getEntityEditHrefBase(entity, location.pathname);
  const idColumnEditHrefBase = editHrefBase
    ? addPrefix(editHrefBase)
    : undefined;
  const idColumnEditHrefSearch = location.search || "";

  const tableWrapperContextValue = useMemo(
    () => ({
      columnDefs: tableDefs as IColumnDef<IExtendableDto, unknown>[],
      groupingField,
      columnGrouping,
      columnOrder,
      columnSorting,
      columnVisibility,
      setColumnGrouping,
      setColumnOrder,
      setColumnSorting,
      setColumnVisibility,
      renderSubComponent: RenderSubComponent as
        RenderSubComponentModel<IExtendableDto> | undefined,
      GridRowRenderer: GridRowRenderer as
        FC<IGridRowProps<IExtendableDto>> | undefined,
      isGridSingleSelectView,
      isRidesEditOrAddRoute,
      idColumnEditHrefBase,
      idColumnEditHrefSearch,
    }),
    // column state objects read from closure when serialized deps change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tableDefsSignature,
      groupingField,
      columnGroupingSignature,
      columnOrderSignature,
      columnSortingSignature,
      columnVisibilitySignature,
      RenderSubComponent,
      GridRowRenderer,
      isGridSingleSelectView,
      isRidesEditOrAddRoute,
      idColumnEditHrefBase,
      idColumnEditHrefSearch,
    ],
  );

  const [outerElement, setOuterElement] = useState<HTMLDivElement | null>(null);

  const tableWindowWrapperContextValue = useMemo(
    () => ({ outerElement }),
    [outerElement],
  );

  const isGroupedTable = groupWithFilteredRows.length > 1;

  usePageScrollPositionMemory({
    element: isGroupedTable ? outerElement : null,
    area: ScrollAreaEnum.GridGrouped,
  });

  /* watching filter changes and resetting selected rows on filter change */
  useWatchingSelectedIds();

  return (
    <TableWindowWrapperContext.Provider value={tableWindowWrapperContextValue}>
      <SearchHighlightProvider>
        <TableWrapperContext.Provider value={tableWrapperContextValue}>
          {isGroupedTable && (
            <TableWrapperVariableSizeList
              groupValues={groupWithFilteredRows}
              setOuterElement={setOuterElement}
              onScrollEnd={onScrollEnd}
            />
          )}
          {groupWithFilteredRows.length === 1 && (
            <TableItemWrapper>
              <TableItemNoGroup groupingValue={groupWithFilteredRows.at(0)} />
            </TableItemWrapper>
          )}
          {!groupWithFilteredRows.length && !isTableGridFetching && (
            <GridNoData />
          )}
        </TableWrapperContext.Provider>
      </SearchHighlightProvider>
    </TableWindowWrapperContext.Provider>
  );
};
